// src/views/AchievementsView.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { AchievementGrid } from '@/components/achievements/AchievementGrid';
import { ProgressTracker } from '@/components/achievements/ProgressTracker';
import { useAuth } from '@/contexts/AuthContext';
import { useTestingData } from '@/hooks/useTestingData';
import { useAchievements } from '@/hooks/useAchievements';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Award } from 'lucide-react';

export const AchievementsView: React.FC = () => {
  const { user } = useAuth();
  const { stats } = useTestingData(user?.uid);
  const { unlockedCount, totalAchievements, progress } = useAchievements(stats);

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">🏆</div>
          <p className="text-muted-foreground">Loading achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <Award className="w-10 h-10 text-yellow-500" />
          Achievements
        </h1>
        <p className="text-muted-foreground">
          Unlock achievements by testing consistently and reaching milestones
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-2 border-primary/50 bg-gradient-to-br from-primary/10 to-primary/5">
          <CardHeader>
            <CardTitle>Overall Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="text-6xl font-bold text-primary mb-2">
                {unlockedCount}
                <span className="text-3xl text-muted-foreground">
                  /{totalAchievements}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Achievements Unlocked
              </p>
            </div>
            <Progress value={progress} className="h-4" />
            <p className="text-sm text-center text-muted-foreground">
              {progress.toFixed(0)}% Complete
            </p>
          </CardContent>
        </Card>

        <ProgressTracker userStats={stats} />
      </div>

      <AchievementGrid userStats={stats} />
    </motion.div>
  );
};

